/**
 * Client mirror of the server's lib/contactEntries.js (RM-189).
 *
 * The restaurant "referral contact" append fields — generalReservationEmailAppends,
 * privateReservationEmailAppends, instantBookingEmailAppends (email) and
 * generalReservationPhoneNumberAppends (phone) — are individual entries:
 *   { name, email }  /  { name, phone }
 * where `name` is an OPTIONAL internal label (not shown in outgoing comms).
 *
 * RM-275 adds a delivery role to EMAIL entries only: { name, email, type } where
 * type is 'to' | 'cc' | 'bcc'. A missing or unrecognized type reads as 'to', so
 * pre-RM-275 records keep their exact behavior. Phone entries carry no role —
 * SMS has no CC/BCC concept.
 *
 * A restaurant may still arrive with a legacy comma-String (un-migrated), so the
 * editors normalize inbound values to rows via parseContactEntries() and strip
 * empty rows on save via cleanContactEntries(). Keep this in sync with
 * lib/contactEntries.js on the server.
 */

/** Delivery roles an email entry may carry, in display order. */
export const CONTACT_TYPES = ['to', 'cc', 'bcc']

/** Human labels for the role toggle. */
export const CONTACT_TYPE_LABELS = { to: 'To', cc: 'Cc', bcc: 'Bcc' }

/**
 * Normalize a stored delivery role. Anything unrecognized falls back to 'to' so
 * a malformed value can never silently hide a recipient from the To line.
 *
 * @param {*} raw
 * @returns {'to'|'cc'|'bcc'}
 */
export function normalizeContactType(raw) {
  if (typeof raw !== 'string') return 'to'
  const normalized = raw.trim().toLowerCase()

  return CONTACT_TYPES.includes(normalized) ? normalized : 'to'
}

/**
 * Normalize an append field (legacy comma-String OR array of entries) into an
 * array of { name, [valueKey] } rows. Values trimmed; empty-value rows dropped.
 *
 * @param {string|Array|null|undefined} value
 * @param {'email'|'phone'} valueKey
 * @returns {Array<{name: string, [k: string]: string}>}
 */
export function parseContactEntries(value, valueKey = 'email') {
  if (value === null || value === undefined) return []

  const carriesType = valueKey === 'email'

  // RM-205: phone entries may carry `automatedTexts` (Profile & Settings checkbox).
  const makeEntry = (name, v, rawType, flags) => {
    const entry = carriesType
      ? { name, [valueKey]: v, type: normalizeContactType(rawType) }
      : { name, [valueKey]: v }
    if (!carriesType && flags && flags.automatedTexts !== undefined) entry.automatedTexts = !!flags.automatedTexts

    return entry
  }

  if (typeof value === 'string') {
    return value
      .split(',')
      .map(v => v.trim())
      .filter(Boolean)
      .map(v => makeEntry('', v, undefined))
  }

  if (Array.isArray(value)) {
    return value
      .map(entry => {
        if (typeof entry === 'string') {
          const v = entry.trim()

          return v ? makeEntry('', v, undefined) : null
        }
        if (entry && typeof entry === 'object') {
          const raw = entry[valueKey]
          const v = typeof raw === 'string' ? raw.trim() : ''
          if (!v) return null
          const name = typeof entry.name === 'string' ? entry.name.trim() : ''

          return makeEntry(name, v, entry.type, entry)
        }

        return null
      })
      .filter(Boolean)
  }

  return []
}

/**
 * Editor helper: normalize to rows but guarantee at least one (blank) row so the
 * UI always shows an empty entry to fill in.
 *
 * @param {string|Array|null|undefined} value
 * @param {'email'|'phone'} valueKey
 * @returns {Array<{name: string, [k: string]: string}>}
 */
export function toEditableRows(value, valueKey = 'email') {
  const rows = parseContactEntries(value, valueKey)

  return rows.length ? rows : [blankRow(valueKey)]
}

/**
 * A fresh empty editor row. New email rows default to 'to'.
 *
 * @param {'email'|'phone'} valueKey
 * @returns {Object}
 */
export function blankRow(valueKey = 'email') {
  return valueKey === 'email'
    ? { name: '', email: '', type: 'to' }
    : { name: '', [valueKey]: '' }
}

/**
 * Save helper: drop rows whose value is empty, trim name + value. Returns a
 * clean array of entries suitable for persisting.
 *
 * @param {Array} rows
 * @param {'email'|'phone'} valueKey
 * @returns {Array<{name: string, [k: string]: string}>}
 */
export function cleanContactEntries(rows, valueKey = 'email') {
  if (!Array.isArray(rows)) return parseContactEntries(rows, valueKey)

  const carriesType = valueKey === 'email'

  return rows
    .map(row => {
      if (!row || typeof row !== 'object') return null
      const v = typeof row[valueKey] === 'string' ? row[valueKey].trim() : ''
      if (!v) return null
      const name = typeof row.name === 'string' ? row.name.trim() : ''

      if (carriesType) return { name, [valueKey]: v, type: normalizeContactType(row.type) }
      const entry = { name, [valueKey]: v }
      if (row.automatedTexts !== undefined) entry.automatedTexts = !!row.automatedTexts

      return entry
    })
    .filter(Boolean)
}

/**
 * Extract just the value strings (emails / phones), accepting either shape.
 *
 * @param {string|Array|null|undefined} value
 * @param {'email'|'phone'} valueKey
 * @returns {string[]}
 */
export function extractContactValues(value, valueKey = 'email') {
  return parseContactEntries(value, valueKey).map(entry => entry[valueKey])
}
